import React, { useCallback, useEffect, useState, useMemo, forwardRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { validateContentRules, validateTronAddressOrHash } from 'src/utils/custom.validator';
import { Form, Input, Button, message, Alert, Typography } from 'antd';
import { NumericFormat } from 'react-number-format';
import Loader from '../loader.component';
import apiCalls from 'src/api/apiCalls';
import { isErrorMessage } from '../shared/error.message';
const { Text } = Typography
const CustomAntdInput = forwardRef((props, ref) => (
    <Input {...props} ref={ref} />
));
CustomAntdInput.displayName = 'CustomAntdInput';
const DepositForm = ({ setIsTopupGridRefresh }) => {
    const [form] = Form.useForm();
    const [creditsAmount, setCreditsAmount] = useState({});
    const [address, setAddress] = useState(null);
    const [addressLoading, setAddressLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchWalletAddress();
    }, []);

    const fetchWalletAddress = async () => {
        setAddressLoading(true);
        try {
            const addr = await apiCalls.getAddress();
            if (addr.ok) {
                setAddress(addr?.data);
            } else {
                setAddress(null);
                setErrorMessage(isErrorMessage(addr));
            }
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setAddressLoading(false);
        }
    };

    // Debounce function
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    };

    const debouncedFetchTRX = useMemo(
        () => debounce(async (creditValue) => {
            if (creditValue) {
                try {
                    const result = await apiCalls.getCreditsAmount(Number(creditValue));
                    setCreditsAmount(result?.data);
                } catch (error) {
                    setErrorMessage(error.message);
                }
            } else {
                setCreditsAmount({});
            }
        }, 200), // 200ms debounce delay
        [] // Empty dependency array means this debounced function is created once
    );

    const handleCreditChange = useCallback(async (e) => {
        const value = e.target.value.replace(/\D/g, ''); // Only numbers
        form.setFieldsValue({ creditNumber: value });
        setCreditsAmount({});
        debouncedFetchTRX(value);
    }, [form, debouncedFetchTRX]);

    const onFinish = useCallback(async (values) => {
        try {
            if (!address?.address) {
                setErrorMessage("There is no deposit address.")
            } else {
                setLoading(true);
                const obj = {
                    "credits": values?.creditNumber,
                    "amount": creditsAmount?.amount,
                    "currency": creditsAmount?.currency,
                    "address": address?.address,
                    "transactionhash": values?.hash,
                    "remarks": values?.remarks
                }
                const response = await apiCalls?.depositSave(obj)
                if (response.ok) {
                    form.resetFields();
                    setCreditsAmount({});
                    fetchWalletAddress();
                    setIsTopupGridRefresh(true);
                    message.success({
                        content: "Credits deposited successfully!",
                        className: "custom-msg",
                        duration: 3,
                    });
                } else {
                    setErrorMessage(isErrorMessage(response));
                }
            }
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setLoading(false);
        }
    }, [creditsAmount, form, address])
    const closeError = useCallback(() => {
        setErrorMessage(null);
    }, []);
    return (

        <>
            <div className="rounded-xl w-full max-w-sm mx-auto">
                <h2 className="text-2xl font-semibold text-titleColor mb-2">
                    Deposit TRX Into This Address
                </h2>
                {addressLoading && <Loader />}
                {!addressLoading &&
                    <>
                        {errorMessage !== null && (
                            <Alert
                                className='mb-6'
                                type="error"
                                description={errorMessage}
                                onClose={closeError}
                                showIcon
                                closable
                            />
                        )
                        }
                        <Form form={form} layout="vertical" onFinish={onFinish} className="space-y-3">
                            <Form.Item
                                className="custom-input mb-3"
                                label="Credit number"
                                name="creditNumber"
                                rules={[{ required: true, message: 'Is required' }]}
                            >
                                <NumericFormat
                                    className="w-full text-subTextColor  rounded-5 px-3 py-2 outline-none !shadow-none"
                                    customInput={CustomAntdInput}
                                    thousandSeparator
                                    placeholder="Enter credit number"
                                    onChange={handleCreditChange}
                                    maxLength={6}
                                />
                            </Form.Item>
                            <div className='!pt-3'>
                                <div className="flex items-center justify-between bg-StrokeColor rounded-5 px-3 py-2 !mt-3">
                                    <span className="text font-bold text-titleColor text-sm flex items-center gap-1">
                                        TRX
                                    </span>
                                    <span className="text font-bold text-subTextColor text-lg">{creditsAmount?.amount || '0'}</span>
                                </div>
                            </div>

                            {/* QR Code */}
                            <div className="flex flex-col items-center my-4">
                                <div className="mb-4 text-center pt-4">

                                    <QRCodeSVG
                                        value={address?.address || '--'}
                                        size={120}
                                        className="qr-image mx-auto w-42 h-42"
                                    />

                                </div>
                            </div>

                            {/* Deposit Address */}
                            <div className="flex items-center justify-between bg-StrokeColor rounded px-3 py-2">
                                <span className="text-paraColor text-base mr-2">
                                    Deposit Address
                                </span>

                                <Text
                                    copyable={{ text: address?.address, tooltips: ['Copy', 'Copied'] }}
                                    className="text-subTextColor text-base truncate max-w-[60%] text-right"
                                >
                                    {address?.address ? `${address?.address.slice(0, 8)}.....${address?.address.slice(-8)}` : '--'}
                                </Text>

                            </div>

                            <Form.Item
                                name="hash"
                                rules={[{ required: true, message: 'Is required' }, { validator: validateTronAddressOrHash }]}
                                label="Hash"
                                className="custom-input mb-3"
                            >
                                <Input
                                    className="w-full rounded-5 px-3 py-2 outline-none !shadow-none"
                                    placeholder="Enter hash"
                                    autoComplete="off"
                                />
                            </Form.Item>

                            <Form.Item
                                name="remarks"
                                rules={[{ validator: validateContentRules }]}
                                label="Remarks"
                                className="custom-input mb-3"
                            >
                                <Input
                                    className="w-full  rounded-5 px-3 py-2 outline-none !shadow-none"
                                    placeholder="Remarks"
                                    autoComplete="off"
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button className="primary-btn w-full" type="primary" htmlType="submit" loading={loading}>
                                    Save
                                </Button>
                            </Form.Item>
                        </Form>
                    </>
                }
            </div>
        </>
    );
};
export default DepositForm;
